import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  SwipeableDrawer
} from '@mui/material';
import {
  Event as EventIcon,
  Checkroom as ClothroomIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  Assignment as AssignmentIcon,
  PhotoLibrary as PhotoLibraryIcon,
  DirectionsCar as DirectionsCarIcon,
  Forum as ForumIcon
} from '@mui/icons-material';
import { GuestEventSchedule } from './GuestEventSchedule';
import { GuestClothingView } from './GuestClothingView';
import { GuestRequestManager } from './GuestRequestManager';
import { GuestProfile } from './GuestProfile';
import { TransportationManager } from './TransportationManager';
import { GuestMediaGallery } from './GuestMediaGallery';
import { MessageBoard } from './MessageBoard';
import { PostDetail } from './PostDetail';
import { CreatePostForm, PostFormData } from './CreatePostForm';

interface Guest {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  oauthProvider: string;
  status: string;
}

interface Event {
  id: string;
  name: string;
  description?: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  isMandatory: boolean;
  isRegistered: boolean;
  registrationStatus: string;
}

interface ClothingAssignment {
  id: string;
  guestId: string;
  eventId: string;
  itemType: string;
  size: string;
  color?: string;
  pickupInstructions?: string;
  status: string;
  eventName?: string;
  eventDate?: string;
}

interface ChangeRequest {
  id: string;
  guestId: string;
  requestType: 'event_change' | 'size_change';
  currentValue: any;
  requestedValue: any;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
}

interface DashboardData {
  guest: Guest;
  events: Event[];
  clothingAssignments: ClothingAssignment[];
  pendingRequests: ChangeRequest[];
  requestHistory: ChangeRequest[];
}

export const GuestDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'clothing' | 'transportation' | 'media' | 'messages' | 'requests' | 'profile'>('events');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [transportationRequestsCount, setTransportationRequestsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [showAllEvents, setShowAllEvents] = useState(true);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [createPostDialogOpen, setCreatePostDialogOpen] = useState(false);
  const [messageBoardRefreshKey, setMessageBoardRefreshKey] = useState(0);
  const [pinnedPost, setPinnedPost] = useState<any>(null);
  const [loadingPinnedPost, setLoadingPinnedPost] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchTransportationRequestsCount = async () => {
    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/transportation/guests/transportation`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const pendingCount = data.data?.filter((req: any) => req.status === 'pending').length || 0;
        setTransportationRequestsCount(pendingCount);
      }
    } catch (err) {
      console.error('Failed to fetch transportation requests count:', err);
      // Don't fail the whole dashboard if transportation count fails
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      // Get last viewed timestamp from localStorage
      const lastViewed = localStorage.getItem('messagesLastViewed') || new Date(0).toISOString();
      
      const response = await fetch(`${apiUrl}/api/message-board/posts?limit=50`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Count posts created after last viewed time
          const unreadCount = result.data.posts?.filter((post: any) => 
            new Date(post.created_at) > new Date(lastViewed)
          ).length || 0;
          setUnreadMessagesCount(unreadCount);
        }
      }
    } catch (err) {
      console.error('Failed to fetch unread messages count:', err);
      // Don't fail the whole dashboard if message count fails
    }
  };

  const fetchPinnedPost = async () => {
    try {
      setLoadingPinnedPost(true);
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      console.log('Fetching pinned post...');
      
      const response = await fetch(`${apiUrl}/api/message-board/posts?limit=50`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Message board posts response:', result);
        console.log('Total posts received:', result.data?.posts?.length);
        
        if (result.success && result.data.posts) {
          // Log all posts with their pinned status for debugging
          result.data.posts.forEach((post: any, index: number) => {
            console.log(`Post ${index}: "${post.title}" - is_pinned:`, post.is_pinned, typeof post.is_pinned);
          });
          
          // Find the first pinned post - check for both boolean true and truthy values
          const pinned = result.data.posts.find((post: any) => {
            const isPinned = post.is_pinned === true || post.is_pinned === 1 || post.is_pinned === '1' || post.is_pinned === 'true';
            if (isPinned) {
              console.log('Found pinned post:', post.title, 'with is_pinned value:', post.is_pinned);
            }
            return isPinned;
          });
          
          console.log('Final pinned post result:', pinned ? pinned.title : 'NONE FOUND');
          setPinnedPost(pinned || null);
        } else {
          console.log('No posts data in response');
          setPinnedPost(null);
        }
      } else {
        console.error('Failed to fetch posts, status:', response.status);
        setPinnedPost(null);
      }
    } catch (err) {
      console.error('Failed to fetch pinned post:', err);
      setPinnedPost(null);
    } finally {
      setLoadingPinnedPost(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use direct API Gateway URL in production to bypass CloudFront caching/routing issues
      const apiUrl = process.env.REACT_APP_API_BASE_URL 
        ? `${process.env.REACT_APP_API_BASE_URL}/guests/dashboard`
        : (process.env.NODE_ENV === 'production' 
          ? 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/guests/dashboard'
          : 'http://localhost:3001/api/guests/dashboard');
      
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(apiUrl, {
        credentials: 'include',
        headers: {
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
          'Content-Type': 'application/json'
        }
      });
      
      // If unauthorized or forbidden, redirect to login
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        window.location.href = '/';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to load dashboard data');
      }
      
      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
        // Fetch counts in parallel
        fetchTransportationRequestsCount();
        fetchUnreadMessagesCount();
        fetchPinnedPost();
      } else {
        throw new Error(result.error?.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      // Check if it's a network/timeout error that might indicate expired credentials
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      if (errorMessage.includes('Failed to load') || errorMessage.includes('Failed to fetch')) {
        // Clear auth and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        window.location.href = '/';
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Mark messages as read when viewing messages tab
  useEffect(() => {
    if (activeTab === 'messages') {
      localStorage.setItem('messagesLastViewed', new Date().toISOString());
      setUnreadMessagesCount(0);
    }
  }, [activeTab]);

  // Periodically check for new messages (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab !== 'messages') {
        fetchUnreadMessagesCount();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [activeTab]);

  const handleLogout = async () => {
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
    fetchPinnedPost();
  };

  const handleCreatePost = async (postData: PostFormData) => {
    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/message-board/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create post');
      }

      setCreatePostDialogOpen(false);
      // Refresh message board
      setMessageBoardRefreshKey(prev => prev + 1);
      setActiveTab('messages');
    } catch (err) {
      throw err;
    }
  };

  const handleViewPost = (postId: string) => {
    setSelectedPostId(postId);
  };

  const handleBackToMessages = () => {
    setSelectedPostId(null);
    // Refresh message board when returning from post detail
    setMessageBoardRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!dashboardData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          No dashboard data available
        </Alert>
      </Container>
    );
  }

  const { 
    guest, 
    events = [], 
    clothingAssignments = [], 
    pendingRequests = [], 
    requestHistory = [] 
  } = dashboardData || {};

  const navigationItems = [
    { id: 'events', label: 'Events', icon: <EventIcon />, badge: events.filter(e => e.isRegistered || e.isMandatory).length },
    { id: 'clothing', label: 'Clothing', icon: <ClothroomIcon />, badge: clothingAssignments.length },
    { id: 'transportation', label: 'Transportation', icon: <DirectionsCarIcon /> },
    { id: 'media', label: 'Photos', icon: <PhotoLibraryIcon /> },
    { id: 'messages', label: 'Messages', icon: <ForumIcon />, badge: unreadMessagesCount },
    { id: 'requests', label: 'Requests', icon: <AssignmentIcon />, badge: pendingRequests.length },
    { id: 'profile', label: 'Profile', icon: <PersonIcon /> }
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const renderDrawerContent = () => (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="h6" noWrap>
          {guest.fullName}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {guest.email}
        </Typography>
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.id}
            component="button"
            selected={activeTab === item.id}
            onClick={() => {
              setActiveTab(item.id as any);
              setDrawerOpen(false);
            }}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main + '20',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '30',
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: activeTab === item.id ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                color: activeTab === item.id ? 'primary.main' : 'inherit',
                fontWeight: activeTab === item.id ? 600 : 400
              }}
            />
            {item.badge && item.badge > 0 && (
              <Chip
                label={item.badge}
                size="small"
                color={activeTab === item.id ? 'primary' : 'default'}
                sx={{ ml: 1 }}
              />
            )}
          </ListItem>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem component="button" onClick={handleRefresh}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <ListItemText primary="Refresh" />
        </ListItem>
        <ListItem component="button" onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1, pb: isMobile ? 7 : 0 }}>
      <AppBar position="static">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <PersonIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }} noWrap>
            {isMobile ? 'Dashboard' : `Welcome, ${guest.fullName}`}
          </Typography>
          {!isMobile && (
            <>
              <IconButton color="inherit" onClick={handleRefresh} title="Refresh">
                <RefreshIcon />
              </IconButton>
              <IconButton color="inherit" onClick={handleLogout} title="Logout">
                <LogoutIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          disableBackdropTransition={!iOS}
          disableDiscovery={iOS}
        >
          {renderDrawerContent()}
        </SwipeableDrawer>
      )}

      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        {/* Dashboard Overview */}
        <Grid container spacing={isSmallMobile ? 2 : 3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => { setActiveTab('events'); setShowAllEvents(true); }}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box display="flex" alignItems="center" flexDirection={isSmallMobile ? 'column' : 'row'}>
                  <EventIcon color="primary" sx={{ mr: isSmallMobile ? 0 : 2, mb: isSmallMobile ? 1 : 0 }} />
                  <Box textAlign={isSmallMobile ? 'center' : 'left'}>
                    <Typography variant={isSmallMobile ? "h5" : "h4"} component="div">
                      {events.length}
                    </Typography>
                    <Typography color="text.secondary" variant={isSmallMobile ? "caption" : "body2"}>
                      Total Events
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => { setActiveTab('events'); setShowAllEvents(false); }}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box display="flex" alignItems="center" flexDirection={isSmallMobile ? 'column' : 'row'}>
                  <EventIcon color="success" sx={{ mr: isSmallMobile ? 0 : 2, mb: isSmallMobile ? 1 : 0 }} />
                  <Box textAlign={isSmallMobile ? 'center' : 'left'}>
                    <Typography variant={isSmallMobile ? "h5" : "h4"} component="div">
                      {events.filter(e => e.isRegistered || e.isMandatory).length}
                    </Typography>
                    <Typography color="text.secondary" variant={isSmallMobile ? "caption" : "body2"}>
                      My Events
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => setActiveTab('clothing')}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box display="flex" alignItems="center" flexDirection={isSmallMobile ? 'column' : 'row'}>
                  <ClothroomIcon color="secondary" sx={{ mr: isSmallMobile ? 0 : 2, mb: isSmallMobile ? 1 : 0 }} />
                  <Box textAlign={isSmallMobile ? 'center' : 'left'}>
                    <Typography variant={isSmallMobile ? "h5" : "h4"} component="div">
                      {clothingAssignments.length}
                    </Typography>
                    <Typography color="text.secondary" variant={isSmallMobile ? "caption" : "body2"}>
                      Clothing Items
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => setActiveTab('requests')}>
              <CardContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                <Box display="flex" alignItems="center" flexDirection={isSmallMobile ? 'column' : 'row'}>
                  <Box sx={{ 
                    mr: isSmallMobile ? 0 : 2, 
                    mb: isSmallMobile ? 1 : 0,
                    width: 24, 
                    height: 24, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main' 
                  }} />
                  <Box textAlign={isSmallMobile ? 'center' : 'left'}>
                    <Typography variant={isSmallMobile ? "h5" : "h4"} component="div">
                      {pendingRequests.length + transportationRequestsCount}
                    </Typography>
                    <Typography color="text.secondary" variant={isSmallMobile ? "caption" : "body2"}>
                      Pending Requests
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Pinned Message Summary - Always visible for debugging */}
        {loadingPinnedPost ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography color="text.secondary">Loading announcements...</Typography>
              </Box>
            </CardContent>
          </Card>
        ) : pinnedPost ? (
          <Card 
            sx={{ 
              mb: 3, 
              bgcolor: 'info.light',
              borderLeft: 4,
              borderColor: 'info.main',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => {
              setActiveTab('messages');
              setSelectedPostId(pinnedPost.id);
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Box
                  sx={{
                    bgcolor: 'info.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <ForumIcon />
                </Box>
                <Box 
                  flex={1}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab('messages');
                    setSelectedPostId(pinnedPost.id);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="overline" color="info.dark" fontWeight="bold">
                      📌 Pinned Announcement
                    </Typography>
                    {unreadMessagesCount > 0 && (
                      <Chip 
                        label={`${unreadMessagesCount} new`} 
                        size="small" 
                        color="error"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {pinnedPost.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {pinnedPost.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Posted by {pinnedPost.author_name} • {new Date(pinnedPost.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab('messages');
                    setSelectedPostId(pinnedPost.id);
                  }}
                >
                  View
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ForumIcon color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    No pinned announcements at this time
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check the message board for updates
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setActiveTab('messages')}
                  sx={{ ml: 'auto' }}
                >
                  View Messages
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Desktop Navigation Tabs */}
        {!isMobile && (
          <Box sx={{ mb: 3 }}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant={activeTab === 'events' ? 'contained' : 'outlined'}
                startIcon={<EventIcon />}
                onClick={() => setActiveTab('events')}
              >
                My Events
              </Button>
              <Button
                variant={activeTab === 'clothing' ? 'contained' : 'outlined'}
                startIcon={<ClothroomIcon />}
                onClick={() => setActiveTab('clothing')}
              >
                Clothing
              </Button>
              <Button
                variant={activeTab === 'transportation' ? 'contained' : 'outlined'}
                startIcon={<DirectionsCarIcon />}
                onClick={() => setActiveTab('transportation')}
              >
                Transportation
              </Button>
              <Button
                variant={activeTab === 'media' ? 'contained' : 'outlined'}
                startIcon={<PhotoLibraryIcon />}
                onClick={() => setActiveTab('media')}
              >
                Photos
              </Button>
              <Button
                variant={activeTab === 'messages' ? 'contained' : 'outlined'}
                startIcon={<ForumIcon />}
                onClick={() => setActiveTab('messages')}
              >
                Messages
                {unreadMessagesCount > 0 && (
                  <Chip
                    label={unreadMessagesCount}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
              <Button
                variant={activeTab === 'requests' ? 'contained' : 'outlined'}
                startIcon={<AssignmentIcon />}
                onClick={() => setActiveTab('requests')}
              >
                Requests
                {pendingRequests.length > 0 && (
                  <Chip
                    label={pendingRequests.length}
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Button>
              <Button
                variant={activeTab === 'profile' ? 'contained' : 'outlined'}
                startIcon={<PersonIcon />}
                onClick={() => setActiveTab('profile')}
              >
                Profile
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab Content */}
        {activeTab === 'events' && (
          <GuestEventSchedule 
            events={showAllEvents ? events : events.filter(e => e.isRegistered || e.isMandatory)}
            pendingRequests={pendingRequests}
            onRefresh={handleRefresh}
            showAllEvents={showAllEvents}
          />
        )}
        
        {activeTab === 'clothing' && (
          <GuestClothingView 
            clothingAssignments={clothingAssignments}
            pendingRequests={pendingRequests}
            onRefresh={handleRefresh}
          />
        )}
        
        {activeTab === 'transportation' && (
          <TransportationManager 
            guestId={guest.id}
          />
        )}
        
        {activeTab === 'media' && (
          <GuestMediaGallery />
        )}
        
        {activeTab === 'messages' && (
          <>
            {selectedPostId ? (
              <PostDetail
                postId={selectedPostId}
                currentUserId={guest.id}
                isAdmin={false}
                onBack={handleBackToMessages}
              />
            ) : (
              <MessageBoard
                key={messageBoardRefreshKey}
                onCreatePost={() => setCreatePostDialogOpen(true)}
                onViewPost={handleViewPost}
              />
            )}
          </>
        )}
        
        {activeTab === 'requests' && (
          <GuestRequestManager 
            pendingRequests={pendingRequests}
            requestHistory={requestHistory}
            onRefresh={handleRefresh}
          />
        )}
        
        {activeTab === 'profile' && (
          <GuestProfile 
            guest={guest}
            onRefresh={handleRefresh}
          />
        )}
      </Container>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <BottomNavigation
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0 8px',
            }
          }}
        >
          <BottomNavigationAction
            label="Events"
            value="events"
            icon={
              <Box sx={{ position: 'relative' }}>
                <EventIcon />
                {events.filter(e => e.isRegistered).length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: 'success.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {events.filter(e => e.isRegistered).length}
                  </Box>
                )}
              </Box>
            }
          />
          <BottomNavigationAction
            label="Clothing"
            value="clothing"
            icon={
              <Box sx={{ position: 'relative' }}>
                <ClothroomIcon />
                {clothingAssignments.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {clothingAssignments.length}
                  </Box>
                )}
              </Box>
            }
          />
          <BottomNavigationAction
            label="Transport"
            value="transportation"
            icon={<DirectionsCarIcon />}
          />
          <BottomNavigationAction
            label="Photos"
            value="media"
            icon={<PhotoLibraryIcon />}
          />
          <BottomNavigationAction
            label="Messages"
            value="messages"
            icon={
              <Box sx={{ position: 'relative' }}>
                <ForumIcon />
                {unreadMessagesCount > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: 'error.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {unreadMessagesCount}
                  </Box>
                )}
              </Box>
            }
          />
          <BottomNavigationAction
            label="Requests"
            value="requests"
            icon={
              <Box sx={{ position: 'relative' }}>
                <AssignmentIcon />
                {pendingRequests.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      backgroundColor: 'warning.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {pendingRequests.length}
                  </Box>
                )}
              </Box>
            }
          />
          <BottomNavigationAction
            label="Profile"
            value="profile"
            icon={<PersonIcon />}
          />
        </BottomNavigation>
      )}

      {/* Create Post Dialog */}
      <CreatePostForm
        open={createPostDialogOpen}
        isAdmin={false}
        onClose={() => setCreatePostDialogOpen(false)}
        onSubmit={handleCreatePost}
      />
    </Box>
  );
};

// iOS detection for SwipeableDrawer optimization
const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);